import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { EditIcon, DeleteIcon } from '../components/Icons';
import { FaCopy } from 'react-icons/fa';
import { EU_COUNTRIES, getCountryName } from '../data/euCountries';
import './AdminShippingMethods.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminShippingMethods() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [editingMethod, setEditingMethod] = useState(null);
  const [editForm, setEditForm] = useState({
    name_lt: '',
    name_en: '',
    name_de: '',
    description_lt: '',
    description_en: '',
    description_de: '',
    cost: 0,
    base_cost: 0,
    cost_per_kg: null,
    free_shipping_threshold: null,
    carrier_name: '',
    estimated_days: null,
    is_express: false,
    requires_signature: false,
    insurance_available: false,
    is_active: true,
    sort_order: 0,
    config_data: {}
  });
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [selectedMethodId, setSelectedMethodId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeLang, setActiveLang] = useState('lt');

  const [page, setPage] = useState(1);

  const { data: methodsData, isLoading } = useQuery({
    queryKey: ['admin-shipping-methods', page],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/shipping-methods?page=${page}&limit=50`);
      return res.data;
    },
  });

  const methods = methodsData?.methods || [];

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/shipping-methods/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-shipping-methods']);
      setEditingMethod(null);
      setShowCreateModal(false);
      notification.success('Pristatymo metodas sėkmingai atnaujintas');
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || 'Klaida atnaujinant pristatymo metodą');
    },
  });

  const deleteMethodMutation = useMutation({
    mutationFn: async (id) => apiClient.delete(`/admin/shipping-methods/${id}`),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries(['admin-shipping-methods']);
      if (selectedMethodId === deletedId) {
        setSelectedMethodId(null);
        setShowRulesModal(false);
      }
      notification.success('Pristatymo metodas pašalintas');
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || 'Klaida šalinant pristatymo metodą');
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/shipping-methods', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-shipping-methods']);
      setEditingMethod(null);
      setShowCreateModal(false);
      setEditForm({
        name_lt: '',
        name_en: '',
        name_de: '',
        description_lt: '',
        description_en: '',
        description_de: '',
        cost: 0,
        base_cost: 0,
        cost_per_kg: null,
        free_shipping_threshold: null,
        carrier_name: '',
        estimated_days: null,
        is_express: false,
        requires_signature: false,
        insurance_available: false,
        is_active: true,
        sort_order: 0,
        config_data: {}
      });
      notification.success('Pristatymo metodas sėkmingai sukurtas');
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || 'Klaida kuriant pristatymo metodą');
    },
  });

  const handleEdit = (method) => {
    setEditingMethod(method);
    let configData = {};
    if (method.config_data) {
      try {
        configData = typeof method.config_data === 'string' ? JSON.parse(method.config_data) : (method.config_data || {});
      } catch {}
    }
    setEditForm({
      name_lt: method.name_lt ?? method.name ?? '',
      name_en: method.name_en ?? '',
      name_de: method.name_de ?? '',
      description_lt: method.description_lt ?? method.description ?? '',
      description_en: method.description_en ?? '',
      description_de: method.description_de ?? '',
      cost: parseFloat(method.cost) || 0,
      base_cost: parseFloat(method.base_cost) || 0,
      cost_per_kg: method.cost_per_kg ? parseFloat(method.cost_per_kg) : null,
      free_shipping_threshold: method.free_shipping_threshold ? parseFloat(method.free_shipping_threshold) : null,
      carrier_name: method.carrier_name ?? '',
      estimated_days: method.estimated_days ? parseInt(method.estimated_days) : null,
      is_express: method.is_express || false,
      requires_signature: method.requires_signature || false,
      insurance_available: method.insurance_available || false,
      is_active: method.is_active !== undefined ? method.is_active : true,
      sort_order: method.sort_order || 0,
      config_data: configData
    });
    setShowCreateModal(true);
  };

  const handleSave = () => {
    const nameLt = (editForm.name_lt || '').trim();
    const nameEn = (editForm.name_en || '').trim();
    const nameDe = (editForm.name_de || '').trim();
    if (!nameLt && !nameEn && !nameDe) {
      notification.warning('Pavadinimas yra privalomas (bent viena kalba)');
      return;
    }

    const isUnisend = !!(editForm.config_data && editForm.config_data.unisend === true);
    const baseCostValue = isUnisend ? 0 : (parseFloat(editForm.base_cost) || parseFloat(editForm.cost) || 0);
    const costPerKgValue = isUnisend ? 0 : (parseFloat(editForm.cost_per_kg) || 0);

    if (!isUnisend && baseCostValue === 0 && costPerKgValue === 0) {
      notification.warning('Reikia nustatyti bent bazinę kainą arba kainą už kg (arba pažymėkite „Kaina iš Unisend API")');
      return;
    }

    const dataToSend = {
      name_lt: nameLt || null,
      name_en: nameEn || null,
      name_de: nameDe || null,
      description_lt: (editForm.description_lt || '').trim() || null,
      description_en: (editForm.description_en || '').trim() || null,
      description_de: (editForm.description_de || '').trim() || null,
      base_cost: baseCostValue,
      cost: baseCostValue,
      cost_per_kg: costPerKgValue > 0 ? costPerKgValue : null,
      carrier_name: isUnisend ? 'Unisend' : ((editForm.carrier_name || '').trim() || null),
      estimated_days: editForm.estimated_days || null,
      free_shipping_threshold: editForm.free_shipping_threshold || null,
      is_express: editForm.is_express,
      requires_signature: editForm.requires_signature,
      insurance_available: editForm.insurance_available,
      is_active: editForm.is_active,
      sort_order: editForm.sort_order ?? 0,
    };
    if (editForm.config_data && Object.keys(editForm.config_data).length > 0) {
      dataToSend.config_data = editForm.config_data;
    }

    if (editingMethod) {
      updateMutation.mutate({ id: editingMethod.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const handleNew = () => {
    setEditingMethod(null);
    setEditForm({
      name_lt: '',
      name_en: '',
      name_de: '',
      description_lt: '',
      description_en: '',
      description_de: '',
      cost: 0,
      base_cost: 0,
      cost_per_kg: null,
      free_shipping_threshold: null,
      carrier_name: '',
      estimated_days: null,
      is_express: false,
      requires_signature: false,
      insurance_available: false,
      is_active: true,
      sort_order: 0,
      config_data: {}
    });
    setShowCreateModal(true);
  };

  const setConfigData = (key, value) => {
    setEditForm(prev => ({
      ...prev,
      config_data: { ...(prev.config_data || {}), [key]: value }
    }));
  };

  if (isLoading) {
    return <div className="admin-shipping-methods-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-shipping-methods-page container">
      <div className="methods-header">
        <h1>Pristatymo metodai</h1>
        <button onClick={handleNew} className="btn btn-primary">
          + Pridėti metodą
        </button>
      </div>

      {methods && methods.length === 0 ? (
        <div className="empty-state">
          <p>Pristatymo metodų nėra. Sukurkite pirmąjį pristatymo metodą.</p>
        </div>
      ) : (
        <>
          <div className="methods-list">
            {methods.map((method) => (
            <div key={method.id} className={`method-card ${!method.is_valid ? 'invalid' : ''}`}>
              <div className="method-info">
                <h3>
                  {method.name_lt || method.name_en || method.name_de || method.name}
                  {!method.is_valid && (
                    <span className="warning-badge">⚠️ Netinkamas</span>
                  )}
                </h3>
                {(method.description_lt || method.description_en || method.description_de || method.description) && (
                  <p className="method-description">
                    {method.description_lt || method.description_en || method.description_de || method.description}
                  </p>
                )}
                <div className="method-details">
                  {method.carrier_name === 'Unisend' ? (
                    <div className="detail-item">
                      <span className="label">Kaina:</span>
                      <span className="value">Iš Unisend API (skaičiuojama automatiškai)</span>
                    </div>
                  ) : method.has_base_cost ? (
                    <div className="detail-item">
                      <span className="label">Bazinė kaina:</span>
                      <span className="value">{parseFloat(method.base_cost || method.cost || 0).toFixed(2)} €</span>
                    </div>
                  ) : (
                    <div className="detail-item">
                      <span className="label">Bazinė kaina:</span>
                      <span className="value warning">Nenustatyta</span>
                    </div>
                  )}
                {method.cost_per_kg && (
                  <div className="detail-item">
                    <span className="label">Kaina už kg:</span>
                    <span className="value">{parseFloat(method.cost_per_kg).toFixed(2)} €</span>
                  </div>
                )}
                {method.free_shipping_threshold && (
                  <div className="detail-item">
                    <span className="label">Nemokamas pristatymas nuo:</span>
                    <span className="value">{parseFloat(method.free_shipping_threshold).toFixed(2)} €</span>
                  </div>
                )}
                {method.estimated_days && (
                  <div className="detail-item">
                    <span className="label">Numatomas laikas:</span>
                    <span className="value">{method.estimated_days} dienos</span>
                  </div>
                )}
                {method.carrier_name && (
                  <div className="detail-item">
                    <span className="label">Vežėjas:</span>
                    <span className="value">{method.carrier_name}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="label">Taisyklės:</span>
                  <span className="value">
                    {method.rules_count || 0} {method.rules_count === 1 ? 'taisyklė' : 'taisyklių'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Statusas:</span>
                  <span className={`status-badge ${method.is_active ? 'active' : 'inactive'}`}>
                    {method.is_active ? 'Aktyvus' : 'Neaktyvus'}
                  </span>
                </div>
                {!method.is_valid && (
                  <div className="detail-item">
                    <span className="label warning">⚠️ Dėmesio:</span>
                    <span className="value warning">
                      Metodas neturi nustatytos kainos arba taisyklių
                    </span>
                  </div>
                )}
                </div>
              </div>
              <div className="method-actions">
              <button
                onClick={() => {
                  setSelectedMethodId(method.id);
                  setShowRulesModal(true);
                }}
                className="btn btn-secondary"
              >
                Taisyklės
              </button>
              <button
                onClick={() => handleEdit(method)}
                className="btn btn-primary"
              >
                Redaguoti
              </button>
              <button
                onClick={async () => {
                  const confirmed = await notification.showConfirm(
                    'Ar tikrai norite pašalinti šį pristatymo metodą? Susijusios taisyklės taip pat bus pašalintos.',
                    'Patvirtinti pristatymo metodo pašalinimą'
                  );
                  if (confirmed) deleteMethodMutation.mutate(method.id);
                }}
                className="btn btn-danger"
                disabled={deleteMethodMutation.isLoading}
                title="Pašalinti metodą"
              >
                Pašalinti
              </button>
              </div>
            </div>
          ))}
          </div>
          {methodsData?.pagination && methodsData.pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary"
              >
                Atgal
              </button>
              <span>
                Puslapis {methodsData.pagination.page} iš {methodsData.pagination.pages} ({methodsData.pagination.total} metodų)
              </span>
              <button
                onClick={() => setPage(p => Math.min(methodsData.pagination.pages, p + 1))}
                disabled={page === methodsData.pagination.pages}
                className="btn btn-secondary"
              >
                Pirmyn
              </button>
            </div>
          )}
        </>
      )}

      {/* Redagavimo/Sukūrimo modalas */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => {
          setEditingMethod(null);
          setShowCreateModal(false);
          setEditForm({
            name: '',
            description: '',
            cost: 0,
            base_cost: 0,
            cost_per_kg: null,
            free_shipping_threshold: null,
            carrier_name: '',
            estimated_days: null,
            is_express: false,
            requires_signature: false,
            insurance_available: false,
            is_active: true,
            sort_order: 0
          });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingMethod ? 'Redaguoti pristatymo metodą' : 'Naujas pristatymo metodas'}</h2>

            <p className="admin-lang-hint">Pavadinimas ir aprašymas – įveskite bent viena kalba (LT / EN / DE).</p>
            <div className="admin-lang-tabs">
              {LOCALES.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
                  onClick={() => setActiveLang(code)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="form-group">
              <label>Pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label}) *</label>
              <input
                type="text"
                value={editForm[`name_${activeLang}`] ?? ''}
                onChange={(e) => setEditForm({ ...editForm, [`name_${activeLang}`]: e.target.value })}
                className="input"
                placeholder={activeLang === 'lt' ? 'Pavadinimas' : activeLang === 'en' ? 'Name' : 'Bezeichnung'}
              />
            </div>

            <div className="form-group">
              <label>Aprašymas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
              <textarea
                value={editForm[`description_${activeLang}`] ?? ''}
                onChange={(e) => setEditForm({ ...editForm, [`description_${activeLang}`]: e.target.value })}
                className="input"
                rows="3"
                placeholder={activeLang === 'lt' ? 'Aprašymas' : activeLang === 'en' ? 'Description' : 'Beschreibung'}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Bazinė kaina (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.base_cost}
                  onChange={(e) => setEditForm({ ...editForm, base_cost: parseFloat(e.target.value) || 0 })}
                  className="input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Kaina už kg (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.cost_per_kg || ''}
                  onChange={(e) => setEditForm({ ...editForm, cost_per_kg: e.target.value ? parseFloat(e.target.value) : null })}
                  className="input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nemokamas pristatymas nuo (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.free_shipping_threshold || ''}
                  onChange={(e) => setEditForm({ ...editForm, free_shipping_threshold: e.target.value ? parseFloat(e.target.value) : null })}
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>Numatomas laikas (dienos)</label>
                <input
                  type="number"
                  value={editForm.estimated_days || ''}
                  onChange={(e) => setEditForm({ ...editForm, estimated_days: e.target.value ? parseInt(e.target.value) : null })}
                  className="input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Vežėjas</label>
              <input
                type="text"
                value={editForm.carrier_name ?? ''}
                onChange={(e) => setEditForm({ ...editForm, carrier_name: e.target.value })}
                className="input"
                placeholder="pvz. LP Express, DPD, Omniva"
                disabled={!!(editForm.config_data && editForm.config_data.unisend)}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={!!(editForm.config_data && editForm.config_data.unisend)}
                  onChange={(e) => {
                    const unisend = e.target.checked;
                    setConfigData('unisend', unisend);
                    if (unisend) {
                      setConfigData('unisend_plan_code', editForm.config_data?.unisend_plan_code || 'TERMINAL');
                      setConfigData('unisend_parcel_type', editForm.config_data?.unisend_parcel_type || 'T2T');
                      setConfigData('unisend_size', editForm.config_data?.unisend_size || 'S');
                    }
                  }}
                />
                <span>Kaina iš Unisend API (Lietuvos paštas) – skaičiuojama automatiškai parduotuvėje</span>
              </label>
            </div>
            {(editForm.config_data && editForm.config_data.unisend) && (
              <div className="form-row" style={{ gap: '12px', flexWrap: 'wrap' }}>
                <div className="form-group">
                  <label>Unisend planas (planCode)</label>
                  <input
                    type="text"
                    value={editForm.config_data.unisend_plan_code || 'TERMINAL'}
                    onChange={(e) => setConfigData('unisend_plan_code', e.target.value)}
                    className="input"
                    placeholder="TERMINAL, HANDS, ..."
                  />
                </div>
                <div className="form-group">
                  <label>Unisend tipas (parcelType)</label>
                  <input
                    type="text"
                    value={editForm.config_data.unisend_parcel_type || 'T2T'}
                    onChange={(e) => setConfigData('unisend_parcel_type', e.target.value)}
                    className="input"
                    placeholder="T2T, T2H, H2H, ..."
                  />
                </div>
                <div className="form-group">
                  <label>Dydis (size)</label>
                  <select
                    value={editForm.config_data.unisend_size || 'S'}
                    onChange={(e) => setConfigData('unisend_size', e.target.value)}
                    className="input"
                  >
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                  </select>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Rikiavimo eilės numeris</label>
              <input
                type="number"
                value={editForm.sort_order}
                onChange={(e) => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) || 0 })}
                className="input"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_express}
                  onChange={(e) => setEditForm({ ...editForm, is_express: e.target.checked })}
                />
                <span>Ekspresinis pristatymas</span>
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.requires_signature}
                  onChange={(e) => setEditForm({ ...editForm, requires_signature: e.target.checked })}
                />
                <span>Reikalingas parašas</span>
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.insurance_available}
                  onChange={(e) => setEditForm({ ...editForm, insurance_available: e.target.checked })}
                />
                <span>Galimas draudimas</span>
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                <span>Aktyvus</span>
              </label>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setEditingMethod(null);
                  setShowCreateModal(false);
                setEditForm({
                  name_lt: '',
                  name_en: '',
                  name_de: '',
                  description_lt: '',
                  description_en: '',
                  description_de: '',
                  cost: 0,
                  base_cost: 0,
                  cost_per_kg: null,
                  free_shipping_threshold: null,
                  carrier_name: '',
                  estimated_days: null,
                  is_express: false,
                  requires_signature: false,
                  insurance_available: false,
                  is_active: true,
                  sort_order: 0
                });
              }}
              className="btn btn-secondary"
            >
              Atšaukti
            </button>
            <button
              type="button"
              onClick={handleSave}
                disabled={updateMutation.isLoading || createMutation.isLoading || !((editForm.name_lt || '').trim() || (editForm.name_en || '').trim() || (editForm.name_de || '').trim())}
                className="btn btn-primary"
              >
                {updateMutation.isLoading || createMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Taisyklių modalas */}
      {showRulesModal && (
        <ShippingRulesModal
          methodId={selectedMethodId}
          onClose={() => {
            setShowRulesModal(false);
            setSelectedMethodId(null);
          }}
        />
      )}
    </div>
  );
}

const PACKAGE_SIZES = ['xs', 's', 'm', 'l', 'xl'];
const DEFAULT_SIZE_FIELDS = { length: null, width: null, height: null, max_weight: null, cost: null };
const DEFAULT_PACKAGE_CONFIG = Object.fromEntries(
  PACKAGE_SIZES.map((s) => [s, { ...DEFAULT_SIZE_FIELDS }])
);

function PackageSizesToggle({ editForm, setEditForm }) {
  const [expanded, setExpanded] = useState({});
  const packageSizeConfig = editForm.package_size_config || { ...DEFAULT_PACKAGE_CONFIG };

  const toggle = (size) => {
    setExpanded((prev) => ({ ...prev, [size]: !prev[size] }));
  };

  const updateSize = (size, field, value) => {
    const newConfig = { ...packageSizeConfig };
    newConfig[size] = {
      ...(newConfig[size] || {}),
      [field]: value
    };
    setEditForm({ ...editForm, package_size_config: newConfig });
  };

  const hasValues = (size) => {
    const c = packageSizeConfig[size];
    return c && (c.length != null || c.width != null || c.height != null || c.max_weight != null || c.cost != null);
  };

  return (
    <div className="form-section package-sizes-toggle" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
      <h4 style={{ marginBottom: '16px' }}>Siuntos dydžiai</h4>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
        Pasirinkite dydį, nustatykite matmenis (cm), max. svorį (kg) ir kainą (€) – kaina gali skirtis pagal dydį.
      </p>
      {PACKAGE_SIZES.map((size) => {
        const isOpen = expanded[size];
        const filled = hasValues(size);
        return (
          <div key={size} className="package-size-toggle-item" style={{ marginBottom: '8px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
            <button
              type="button"
              onClick={() => toggle(size)}
              className="package-size-toggle-header"
              style={{
                width: '100%',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: filled ? '#e8f5e9' : '#f5f5f5',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                textTransform: 'uppercase',
                textAlign: 'left'
              }}
            >
              <span>{size.toUpperCase()}</span>
              <span style={{ fontSize: '18px', opacity: 0.7 }}>{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && (
              <div className="package-size-toggle-body" style={{ padding: '16px', background: '#fafafa' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Ilgis (cm)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={packageSizeConfig[size]?.length ?? ''}
                      onChange={(e) => updateSize(size, 'length', e.target.value ? parseFloat(e.target.value) : null)}
                      className="input"
                      placeholder="cm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Plotis (cm)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={packageSizeConfig[size]?.width ?? ''}
                      onChange={(e) => updateSize(size, 'width', e.target.value ? parseFloat(e.target.value) : null)}
                      className="input"
                      placeholder="cm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Aukštis (cm)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={packageSizeConfig[size]?.height ?? ''}
                      onChange={(e) => updateSize(size, 'height', e.target.value ? parseFloat(e.target.value) : null)}
                      className="input"
                      placeholder="cm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Max. svoris (kg)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={packageSizeConfig[size]?.max_weight ?? ''}
                      onChange={(e) => updateSize(size, 'max_weight', e.target.value ? parseFloat(e.target.value) : null)}
                      className="input"
                      placeholder="kg"
                    />
                  </div>
                  <div className="form-group">
                    <label>Kaina (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={packageSizeConfig[size]?.cost ?? ''}
                      onChange={(e) => updateSize(size, 'cost', e.target.value ? parseFloat(e.target.value) : null)}
                      className="input"
                      placeholder="pvz. 4.99"
                    />
                    <span style={{ fontSize: '12px', color: '#666' }}>Jei tuščia – naudojama taisyklės bazinė kaina</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Taisyklių modalas komponentas
function ShippingRulesModal({ methodId, onClose }) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeLang, setActiveLang] = useState('lt');
  const [editForm, setEditForm] = useState({
    name_lt: '',
    name_en: '',
    name_de: '',
    description_lt: '',
    description_en: '',
    description_de: '',
    region_type: 'country',
    region_value: '',
    min_weight: null,
    max_weight: null,
    min_order_value: null,
    cost: 0,
    cost_per_kg: null,
    free_shipping_threshold: null,
    estimated_days: null,
    is_active: true,
    priority: 0,
    package_size_config: { ...DEFAULT_PACKAGE_CONFIG }
  });
  const [editingRule, setEditingRule] = useState(null);

  const { data: rules, isLoading } = useQuery({
    queryKey: ['shipping-rules', methodId],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/shipping-rules/method/${methodId}`);
      return res.data;
    },
    enabled: !!methodId,
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/shipping-rules', { ...data, shipping_method_id: methodId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping-rules', methodId]);
      setShowCreateModal(false);
      setEditForm(getEmptyRulesForm());
      notification.success('Taisyklė sėkmingai sukurta');
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || 'Klaida kuriant taisyklę');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/shipping-rules/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping-rules', methodId]);
      setEditingRule(null);
      notification.success('Taisyklė sėkmingai atnaujinta');
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || 'Klaida atnaujinant taisyklę');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/shipping-rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping-rules', methodId]);
      notification.success('Taisyklė pašalinta');
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || 'Klaida šalinant taisyklę');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.post(`/admin/shipping-rules/${id}/duplicate`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping-rules', methodId]);
      notification.success('Taisyklė sėkmingai dubliuota');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida dubliuojant taisyklę');
    },
  });

  const handleEdit = (rule) => {
    setEditingRule(rule);
    
    // Parse package_size_config (įskaitant cost pagal dydį)
    let packageSizeConfig = { ...DEFAULT_PACKAGE_CONFIG };
    if (rule.package_size_config) {
      try {
        const parsed = typeof rule.package_size_config === 'string'
          ? JSON.parse(rule.package_size_config)
          : rule.package_size_config;
        PACKAGE_SIZES.forEach((s) => {
          packageSizeConfig[s] = { ...DEFAULT_SIZE_FIELDS, ...(parsed[s] || {}) };
        });
      } catch (e) {
      }
    }
    
    setEditForm({
      name_lt: rule.name_lt ?? '',
      name_en: rule.name_en ?? '',
      name_de: rule.name_de ?? '',
      description_lt: rule.description_lt ?? '',
      description_en: rule.description_en ?? '',
      description_de: rule.description_de ?? '',
      region_type: rule.region_type || 'country',
      region_value: rule.region_value || '',
      min_weight: rule.min_weight ? parseFloat(rule.min_weight) : null,
      max_weight: rule.max_weight ? parseFloat(rule.max_weight) : null,
      min_order_value: rule.min_order_value ? parseFloat(rule.min_order_value) : null,
      cost: parseFloat(rule.cost) || 0,
      cost_per_kg: rule.cost_per_kg ? parseFloat(rule.cost_per_kg) : null,
      free_shipping_threshold: rule.free_shipping_threshold ? parseFloat(rule.free_shipping_threshold) : null,
      estimated_days: rule.estimated_days ? parseInt(rule.estimated_days) : null,
      is_active: rule.is_active !== undefined ? rule.is_active : true,
      priority: rule.priority || 0,
      package_size_config: packageSizeConfig
    });
    setShowCreateModal(true);
  };

  function getEmptyRulesForm() {
    return {
      name_lt: '',
      name_en: '',
      name_de: '',
      description_lt: '',
      description_en: '',
      description_de: '',
      region_type: 'country',
      region_value: '',
      min_weight: null,
      max_weight: null,
      min_order_value: null,
      cost: 0,
      cost_per_kg: null,
      free_shipping_threshold: null,
      estimated_days: null,
      is_active: true,
      priority: 0,
      package_size_config: { ...DEFAULT_PACKAGE_CONFIG }
    };
  }

  const handleSave = () => {
    // Siųsti package_size_config jei yra bent viena reikšmė (matmenys, svoris arba kaina pagal dydį)
    let packageSizeConfigToSend = null;
    if (editForm.package_size_config) {
      const hasAnyValue = Object.values(editForm.package_size_config).some(sizeConfig =>
        sizeConfig && (
          (sizeConfig.length != null) || (sizeConfig.width != null) || (sizeConfig.height != null) ||
          (sizeConfig.max_weight != null) || (sizeConfig.cost != null)
        )
      );
      if (hasAnyValue) {
        packageSizeConfigToSend = editForm.package_size_config;
      }
    }
    
    const dataToSend = {
      ...editForm,
      package_size_config: packageSizeConfigToSend
    };
    
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  if (isLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div>Kraunama...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Pristatymo taisyklės</h2>
          <button onClick={onClose} className="btn-close">×</button>
        </div>

        <div className="rules-actions">
          <button
            onClick={() => {
              setEditingRule(null);
              setEditForm(getEmptyRulesForm());
              setShowCreateModal(true);
            }}
            className="btn btn-primary"
          >
            + Pridėti taisyklę
          </button>
        </div>

        <div className="rules-table-wrapper">
          <table className="rules-table">
            <thead>
              <tr>
                <th>Pavadinimas</th>
                <th>Regionas</th>
                <th>Min. svoris</th>
                <th>Max. svoris</th>
                <th>Min. suma</th>
                <th>Kaina</th>
                <th>Kaina/kg</th>
                <th>Nemokamas nuo</th>
                <th>Prioritetas</th>
                <th>Statusas</th>
                <th>Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {rules?.map((rule) => (
                <tr key={rule.id}>
                  <td>{rule.name_lt || rule.name_en || rule.name_de || '—'}</td>
                  <td>
                    <strong>{rule.region_type === 'country' ? 'Šalis' : rule.region_type === 'city' ? 'Miestas' : rule.region_type === 'postal_code' ? 'Pašto kodas' : rule.region_type}</strong>
                    {rule.region_value && (
                      <div>
                        {rule.region_type === 'country' && rule.region_value.length === 2
                          ? `${getCountryName(rule.region_value, 'lt')} (${rule.region_value})`
                          : rule.region_value}
                      </div>
                    )}
                  </td>
                  <td>{rule.min_weight ? `${rule.min_weight} kg` : '—'}</td>
                  <td>{rule.max_weight ? `${rule.max_weight} kg` : '—'}</td>
                  <td>{rule.min_order_value ? `${rule.min_order_value} €` : '—'}</td>
                  <td>{parseFloat(rule.cost).toFixed(2)} €</td>
                  <td>{rule.cost_per_kg ? `${rule.cost_per_kg} €` : '—'}</td>
                  <td>{rule.free_shipping_threshold ? `${rule.free_shipping_threshold} €` : '—'}</td>
                  <td>{rule.priority}</td>
                  <td>
                    <span className={`status-badge ${rule.is_active ? 'active' : 'inactive'}`}>
                      {rule.is_active ? 'Aktyvus' : 'Neaktyvus'}
                    </span>
                  </td>
                  <td>
                    <div className="rules-row-actions">
                      <button
                        type="button"
                        onClick={() => handleEdit(rule)}
                        className="btn-icon btn-icon-secondary"
                        title="Redaguoti"
                        aria-label="Redaguoti"
                      >
                        <EditIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => duplicateMutation.mutate(rule.id)}
                        className="btn-icon btn-icon-secondary"
                        title="Dubliuoti taisyklę"
                        aria-label="Dubliuoti"
                      >
                        <FaCopy size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmed = await notification.showConfirm(
                            'Ar tikrai norite pašalinti šią taisyklę?',
                            'Patvirtinti taisyklės pašalinimą'
                          );
                          if (confirmed) deleteMutation.mutate(rule.id);
                        }}
                        className="btn-icon btn-icon-danger"
                        title="Pašalinti"
                        aria-label="Pašalinti"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>{editingRule ? 'Redaguoti taisyklę' : 'Nauja taisyklė'}</h3>

              <p className="admin-lang-hint">Pavadinimas ir aprašymas – įveskite bent viena kalba (LT / EN / DE). Neprivaloma.</p>
              <div className="admin-lang-tabs">
                {LOCALES.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
                    onClick={() => setActiveLang(code)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="form-group">
                <label>Pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                <input
                  type="text"
                  value={editForm[`name_${activeLang}`] ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, [`name_${activeLang}`]: e.target.value })}
                  className="input"
                  placeholder={activeLang === 'lt' ? 'Pavadinimas' : activeLang === 'en' ? 'Name' : 'Bezeichnung'}
                />
              </div>

              <div className="form-group">
                <label>Aprašymas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
                <textarea
                  value={editForm[`description_${activeLang}`] ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, [`description_${activeLang}`]: e.target.value })}
                  className="input"
                  rows="2"
                  placeholder={activeLang === 'lt' ? 'Aprašymas' : activeLang === 'en' ? 'Description' : 'Beschreibung'}
                />
              </div>

              <div className="form-group">
                <label>Regiono tipas *</label>
                <select
                  value={editForm.region_type}
                  onChange={(e) => setEditForm({ ...editForm, region_type: e.target.value })}
                  className="input"
                >
                  <option value="country">Šalis</option>
                  <option value="city">Miestas</option>
                  <option value="postal_code">Pašto kodas</option>
                </select>
              </div>

              <div className="form-group">
                <label>Regiono reikšmė *</label>
                {editForm.region_type === 'country' ? (
                  <select
                    value={editForm.region_value || ''}
                    onChange={(e) => setEditForm({ ...editForm, region_value: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="">Pasirinkite šalį</option>
                    {EU_COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name_lt} ({c.code})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editForm.region_value}
                    onChange={(e) => setEditForm({ ...editForm, region_value: e.target.value })}
                    className="input"
                    placeholder={editForm.region_type === 'city' ? 'pvz. Vilnius' : 'pvz. 01234'}
                    required
                  />
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kaina (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.cost}
                    onChange={(e) => setEditForm({ ...editForm, cost: parseFloat(e.target.value) || 0 })}
                    className="input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Kaina už kg (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.cost_per_kg || ''}
                    onChange={(e) => setEditForm({ ...editForm, cost_per_kg: e.target.value ? parseFloat(e.target.value) : null })}
                    className="input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nemokamas pristatymas nuo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.free_shipping_threshold || ''}
                    onChange={(e) => setEditForm({ ...editForm, free_shipping_threshold: e.target.value ? parseFloat(e.target.value) : null })}
                    className="input"
                  />
                </div>

                <div className="form-group">
                  <label>Prioritetas</label>
                  <input
                    type="number"
                    value={editForm.priority}
                    onChange={(e) => setEditForm({ ...editForm, priority: parseInt(e.target.value) || 0 })}
                    className="input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  />
                  <span>Aktyvus</span>
                </label>
              </div>

              {/* Siuntos dydžių konfigūracija – toggle stilius */}
              <PackageSizesToggle
                editForm={editForm}
                setEditForm={setEditForm}
              />

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingRule(null);
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  onClick={handleSave}
                  disabled={createMutation.isLoading || updateMutation.isLoading || !editForm.region_value}
                  className="btn btn-primary"
                >
                  {createMutation.isLoading || updateMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminShippingMethods;
